import { Chapter, ChapterPlan, Ledger } from '../src/types';
import {
  auditNarrativeDensity,
  evaluateChapterMomentum,
  getProseControls,
} from '../src/utils/narrativeMomentum.service';
import { evaluatePhysicalContinuityAcceptance } from '../src/utils/physicalContinuity.service';

export interface AcceptanceResult {
  isBlocked: boolean;
  warnings: string[];
  telemetry: {
    wordCount: number;
    pacingDensity: number;
    sceneCount: number;
    firstDisruptionPositionPercent?: number;
    routineProcedurePercent?: number;
    meaningfulStateChanges?: number;
  };
}

/**
 * Normalizes text to treat hyphenations, spacing, and casing as equivalent.
 */
function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
    .replace(/\s+/g, "")
    .trim();
}

/**
 * Extracts normalized keyword variants from a sentence to capture hyphenated/spaced variations.
 */
function getNormalizedKeywords(phrase: string): string[] {
  return phrase
    .split(/\s+/)
    .map(w => w.replace(/[.,\/#!$%\^&\*;:{}=\_`~()]/g, "").trim())
    .filter(w => w.length > 4 && !['chapter', 'character', 'location', 'though', 'having', 'should', 'would', 'could', 'about', 'their'].includes(w.toLowerCase()));
}

/**
 * Executes a deterministic safety check of the generated prose draft against
 * the canon Ledger, Chapter Outline Plan, and general narrative bounds.
 */
export function runAcceptanceChecks(
  draft: Chapter,
  plan: ChapterPlan | undefined,
  ledger: Ledger,
  currentChapterNum: number,
  proseControls = getProseControls(),
): AcceptanceResult {
  const warnings: string[] = [];
  let isBlocked = false;

  const prose = draft.content || '';
  const words = prose.split(/\s+/).filter(Boolean);
  const wordCount = words.length;

  // 1. Improved Dialogue Density Detection (Straight & Curly Quote Support)
  const dialogueRegex = /"([^"\\]|\\.)*"|“([^”\\]|\\.)*”|‘([^’\\]|\\.)*’/g;
  const dialogueMatches = prose.match(dialogueRegex) || [];
  const dialogueWordCount = dialogueMatches.reduce((acc, m) => acc + m.split(/\s+/).filter(Boolean).length, 0);
  const pacingDensity = wordCount > 0 ? parseFloat((dialogueWordCount / wordCount).toFixed(3)) : 0;

  const sceneCount = (draft as any).scenes?.length || 1;

  // ── Blocking Checks ────────────────────────────────────────────────────────
  if (!prose.trim()) {
    isBlocked = true;
    warnings.push("BLOCKING ERROR: Generated prose draft is completely empty.");
  }

  // ── Warning-Only Checks ────────────────────────────────────────────────────
  
  // 1. Word Count check
  if (wordCount < 1500 && prose.trim().length > 0) {
    warnings.push(`Chapter is extremely short (${wordCount} words). Scribe targets at least 2500 words.`);
  }

  // 2. Dead/Deceased Character Presence Check
  const deadCharacters = Object.entries(ledger.characters || {})
    .filter(([_, c]: [string, any]) => {
      const status = (c.status || '').toLowerCase();
      return status.includes('dead') || status.includes('deceased') || status.includes('killed');
    })
    .map(([name]) => name);

  deadCharacters.forEach(charName => {
    const regex = new RegExp(`\\b${charName}\\b`, 'i');
    if (regex.test(prose)) {
      warnings.push(`Continuity Warning: Character "${charName}" is marked Deceased/Dead in your Canon Ledger, but was found active in this draft.`);
    }
  });

  // 3. Robust Roadmap Beat Verification (Hyphen and spacing equivalent check)
  if (plan && plan.requiredBeats && plan.requiredBeats.length > 0) {
    const normalizedProse = normalizeString(prose);

    plan.requiredBeats.forEach(beat => {
      const keywords = getNormalizedKeywords(beat);
      if (keywords.length === 0) return;

      // Check if hyphenated forms or spacings match within normalized prose
      const matchCount = keywords.filter(word => {
        // Match both raw, spaced, and combined forms
        const normalizedWord = normalizeString(word);
        return normalizedProse.includes(normalizedWord);
      }).length;

      // Only warn if less than 35% of the descriptive keywords are matched
      const matchRatio = matchCount / keywords.length;
      if (matchRatio < 0.35 && keywords.length > 2) {
        warnings.push(`Roadmap Beat representation mismatch: "${beat}" (Only matched ${matchCount}/${keywords.length} terms)`);
      }
    });
  }

  // 4. Fine-Tuned Forbidden Moves Validation (Prevent false positives)
  if (plan && plan.forbiddenMoves && plan.forbiddenMoves.length > 0) {
    plan.forbiddenMoves.forEach(move => {
      const keywords = getNormalizedKeywords(move);
      if (keywords.length === 0) return;

      const matches = keywords.filter(word => {
        const regex = new RegExp(`\\b${word}\\b`, 'i');
        return regex.test(prose);
      });

      // Require high keyword density overlap (at least 60% of keywords) to trigger warning of violation
      if (matches.length >= Math.ceil(keywords.length * 0.6) && keywords.length > 2) {
        warnings.push(`Forbidden Move Risk: Content matches high density of keywords from forbidden instruction: "${move}"`);
      }
    });
  }

  // 5. Narrative momentum density audit (warnings; blocking handled in validateChapter)
  const momentumAudit = auditNarrativeDensity(prose, currentChapterNum, proseControls);
  const momentumEval = evaluateChapterMomentum(prose, currentChapterNum, plan, proseControls);
  momentumAudit.warnings?.forEach(w => warnings.push(`Momentum: ${w}`));
  if (momentumEval.shouldBlock) {
    warnings.push(`BLOCKING MOMENTUM: ${momentumEval.reason}`);
  }

  const physicalContinuity = evaluatePhysicalContinuityAcceptance(prose, ledger, currentChapterNum);
  physicalContinuity.warnings.forEach((warning) => warnings.push(warning));
  if (physicalContinuity.isBlocked) {
    isBlocked = true;
  }

  return {
    isBlocked,
    warnings,
    telemetry: {
      wordCount,
      pacingDensity,
      sceneCount,
      firstDisruptionPositionPercent: momentumAudit.firstDisruptionPositionPercent,
      routineProcedurePercent: momentumAudit.routineProcedurePercent,
      meaningfulStateChanges: momentumAudit.meaningfulStateChanges,
    }
  };
}
